import { Label } from "design-system/atoms/Label"
import { SheetDescription, SheetHeader, SheetTitle } from "design-system/atoms/Sheet"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormControl, FormField, FormItem } from "design-system/organisms/Form"
import { ensure } from "lopermut"
import { useCapabilitiesQuery } from "modules/capabilities/hooks/useCapabilitiesQuery"
import { useFrontAppIntegrationQuery } from "modules/settings/hooks/frontapp/useFrontAppIntegrationQuery"
import { useGorgiasIntegrationQuery } from "modules/settings/hooks/gorgias/useGorgiasIntegrationQuery"
import { useZendeskIntegrationQuery } from "modules/settings/hooks/zendesk/useZendeskIntegrationQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Fragment } from "react/jsx-runtime"
import { ActionTypeEnum } from "schema"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { ActionIconMap } from "../maps/actionIconMap"
import { CreateFrontAppTicketConfiguration } from "./actions/CreateFrontAppTicketConfiguration"
import { CreateGorgiasTicketConfiguration } from "./actions/CreateGorgiasTicketConfiguration"
import { CreateZendeskTicketConfiguration } from "./actions/CreateZendeskTicketConfiguration"
import { DisableAgentConfiguration } from "./actions/DisableAgentConfiguration"
import { SendInConversationMessage } from "./actions/SendInConversationMessage"
import { SendInternalEmail } from "./actions/SendInternalEmail"
import { TriggerCapabilityConfiguration } from "./actions/TriggerCapabilityConfiguration"
import { UpdateCustomerTagsConfiguration } from "./actions/UpdateCustomerTagsConfiguration"
import { WebhookConfiguration } from "./actions/WebhookConfiguration"

export function ActionSheet() {
  const { t } = useTranslation()
  const { form, focusedNode } = useWorkflowBuilder()

  const { data: zendeskIntegrationData } = useZendeskIntegrationQuery()
  const { data: gorgiasIntegrationData } = useGorgiasIntegrationQuery()
  const { data: frontAppIntegrationData } = useFrontAppIntegrationQuery()
  const { data: capabilities } = useCapabilitiesQuery()

  const canCreateZendeskTicket = !!zendeskIntegrationData
  const canCreateGorgiasTicket = !!gorgiasIntegrationData
  const canCreateFrontAppTicket = !!frontAppIntegrationData
  const hasWorkflowCapabilities = !!capabilities?.some((c) => c.isAvailableInWorkflows)

  const selectOptions = useMemo(
    () =>
      [
        {
          key: ActionTypeEnum.CreateZendeskTicket,
          isEnabled: canCreateZendeskTicket,
          disabledTooltip: t(
            "campaigns.action.availableActions.CreateZendeskTicket.disabledExplanation",
          ),
        },
        {
          key: ActionTypeEnum.CreateGorgiasTicket,
          isEnabled: canCreateGorgiasTicket,
          disabledTooltip: t(
            "campaigns.action.availableActions.CreateGorgiasTicket.disabledExplanation",
          ),
        },
        {
          key: ActionTypeEnum.CreateFrontAppTicket,
          isEnabled: canCreateFrontAppTicket,
          disabledTooltip: t(
            "campaigns.action.availableActions.CreateFrontAppTicket.disabledExplanation",
          ),
        },
        { key: ActionTypeEnum.SendWebhook, isEnabled: true },
        { key: ActionTypeEnum.SendInternalEmail, isEnabled: true },
        { key: ActionTypeEnum.SendInConversationMessage, isEnabled: true },
        { key: ActionTypeEnum.DisableAgent, isEnabled: true },
        { key: ActionTypeEnum.UpdateCustomerTags, isEnabled: true },
        {
          key: ActionTypeEnum.TriggerCapability,
          isEnabled: hasWorkflowCapabilities,
          disabledTooltip: t(
            "campaigns.action.availableActions.TriggerCapability.disabledExplanation",
          ),
        },
      ] satisfies Array<{
        key: keyof typeof ActionTypeEnum
        isEnabled: boolean
        disabledTooltip?: string
      }>,
    [
      canCreateFrontAppTicket,
      canCreateGorgiasTicket,
      canCreateZendeskTicket,
      hasWorkflowCapabilities,
      t,
    ],
  )

  const nodeIndex = useMemo(
    () => ensure(form.watch("nodes")?.findIndex((node) => node.id === focusedNode?.id)),
    [form, focusedNode],
  )

  const node = form.watch(`nodes.${nodeIndex}`)
  if (!node || !("action" in node)) return null

  const selectedAction = form.watch(`nodes.${nodeIndex}.action`)

  function renderActionConfiguration() {
    if (!node || !("action" in node)) return null

    switch (selectedAction) {
      case "SendWebhook":
        return <WebhookConfiguration nodeIndex={nodeIndex} node={node} />
      case "CreateZendeskTicket":
        return <CreateZendeskTicketConfiguration nodeIndex={nodeIndex} node={node} />
      case "CreateGorgiasTicket":
        return <CreateGorgiasTicketConfiguration nodeIndex={nodeIndex} node={node} />
      case "CreateFrontAppTicket":
        return <CreateFrontAppTicketConfiguration nodeIndex={nodeIndex} node={node} />
      case "SendInternalEmail":
        return <SendInternalEmail nodeIndex={nodeIndex} node={node} />
      case "SendInConversationMessage":
        return <SendInConversationMessage nodeIndex={nodeIndex} node={node} />
      case "TriggerCapability":
        return <TriggerCapabilityConfiguration nodeIndex={nodeIndex} node={node} />
      case "DisableAgent":
        return <DisableAgentConfiguration nodeIndex={nodeIndex} node={node} />
      case "UpdateCustomerTags":
        return <UpdateCustomerTagsConfiguration nodeIndex={nodeIndex} node={node} />
      default:
        return null
    }
  }

  return (
    <SheetHeader className="flex flex-col gap-4 text-left">
      <div className="flex flex-col gap-2">
        <SheetTitle>{t("campaigns.action.label")}</SheetTitle>
        <SheetDescription>{t("campaigns.action.description")}</SheetDescription>
      </div>
      <div className="flex flex-col gap-2">
        <FormField
          control={form.control}
          name={`nodes.${nodeIndex}.action`}
          render={() => {
            const selectedActionLabel = selectedAction
              ? t(`campaigns.action.availableActions.${selectedAction}.title`)
              : ""
            const SelectedActionIcon = selectedAction ? ActionIconMap[selectedAction] : null

            return (
              <FormItem className="flex flex-col gap-2">
                <Label>{t("campaigns.action.label")}</Label>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      form.setValue(
                        `nodes.${nodeIndex}.action`,
                        value as keyof typeof ActionTypeEnum,
                        {
                          shouldDirty: true,
                        },
                      )
                      form.setValue(`nodes.${nodeIndex}.config`, {})
                      form.trigger(`nodes.${nodeIndex}.action`)
                    }}
                    value={selectedAction}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("campaigns.action.placeholder")}>
                        <div className="flex items-center gap-2">
                          {selectedAction && SelectedActionIcon ? (
                            <div className="flex items-center gap-2">
                              <SelectedActionIcon className="size-5" />
                              <span className="font-medium">{selectedActionLabel}</span>
                            </div>
                          ) : (
                            t("campaigns.action.placeholder")
                          )}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {selectOptions.map(({ key, isEnabled, disabledTooltip }, index) => {
                        const Icon = ActionIconMap[key as keyof typeof ActionIconMap]
                        const label = t(`campaigns.action.availableActions.${key}.title`)
                        const description = t(
                          `campaigns.action.availableActions.${key}.description`,
                        )

                        let content = (
                          <SelectItem value={key} disabled={!isEnabled}>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <Icon className="size-5" />
                                <span className="font-medium">{label}</span>
                              </div>
                              <span className="text-sm text-muted-foreground">{description}</span>
                            </div>
                          </SelectItem>
                        )

                        if (!isEnabled && disabledTooltip) {
                          content = (
                            <Tooltip key={key}>
                              <TooltipTrigger className="w-full">{content}</TooltipTrigger>
                              <TooltipContent side="top">{disabledTooltip}</TooltipContent>
                            </Tooltip>
                          )
                        }

                        return (
                          <Fragment key={key}>
                            {content}
                            {index < selectOptions.length - 1 && <SelectSeparator />}
                          </Fragment>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )
          }}
        />
      </div>
      {renderActionConfiguration()}
    </SheetHeader>
  )
}
